import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { schoolApi, type SchoolTeacher, type TeacherStats } from '../../api/school'
import { queryKeys } from '../../api/queryKeys'
import { roleColor, roleLabel } from '../../utils/schoolFormatters'
import { useAuth } from '../../auth/AuthContext'
import { canResetTwoFactor } from '../../utils/twoFactor'

/**
 * Строка таблицы учителей. По клику разворачивается доп. строка
 * со статистикой (lazy — refetch только при первом раскрытии).
 *
 * Для PRINCIPAL+ показываются действия: сменить роль, удалить из школы.
 */
export function TeacherRow({ t, schoolId }: { t: SchoolTeacher; schoolId: string }) {
  const { t: tr } = useTranslation()
  const { teacher: me } = useAuth()
  const qc = useQueryClient()
  const [showStats, setShowStats] = useState(false)
  const canManage = !!me && (me.role === 'PRINCIPAL' || me.role === 'ADMIN') && me.id !== t.id
  const canReset2fa = !!me && !!t.totpEnabled && canResetTwoFactor(me.role, me.id, t.role, t.id)

  const stats = useQuery({
    queryKey: queryKeys.school.teacherStats(schoolId, t.id),
    queryFn: () => schoolApi.teacherStats(schoolId, t.id),
    retry: 0,
    enabled: false,
  })

  const changeRole = useMutation({
    mutationFn: (newRole: string) => schoolApi.changeTeacherRole(schoolId, t.id, newRole),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.school.teachers(schoolId) }),
  })

  const reset2fa = useMutation({
    mutationFn: () => schoolApi.resetTwoFactor(schoolId, t.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.school.teachers(schoolId) }),
  })

  const remove = useMutation({
    mutationFn: () => schoolApi.removeTeacher(schoolId, t.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.school.teachers(schoolId) }),
  })

  const toggle = () => {
    if (!showStats) {
      stats.refetch()
      setShowStats(true)
    } else {
      setShowStats(false)
    }
  }

  return (
    <>
      <tr
        onClick={toggle}
        className="border-t border-gray-100 hover:bg-primary-soft/30 cursor-pointer"
      >
        <td className="px-4 py-2.5 font-medium text-gray-900">{t.displayName}</td>
        <td className="px-4 py-2.5 text-gray-700 hidden md:table-cell">{t.email}</td>
        <td className="px-4 py-2.5">
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${roleColor(t.role)}`}>
            {roleLabel(t.role)}
          </span>
          {t.totpEnabled !== undefined && (
            <span
              className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded font-medium ${
                t.totpEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}
              title={t.totpEnabled ? tr('teacherRow.twoFaOn') : tr('teacherRow.twoFaOff')}
            >
              2FA {t.totpEnabled ? '✓' : '—'}
            </span>
          )}
        </td>
        <td className="px-4 py-2.5 text-gray-500 text-xs hidden lg:table-cell">
          {new Date(t.createdAt).toLocaleDateString('ru')}
        </td>
      </tr>
      {showStats && (
        <tr className="border-t border-gray-100 bg-gray-50">
          <td colSpan={4} className="px-4 py-3">
            <StatsContent isLoading={stats.isLoading} data={stats.data} />
            {canReset2fa && (
              <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-3">
                <span className="text-xs text-gray-500">{tr('teacherRow.reset2faHint')}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm(tr('teacherRow.reset2faConfirm', { name: t.displayName }))) reset2fa.mutate()
                  }}
                  disabled={reset2fa.isPending}
                  className="text-xs text-red-600 hover:text-red-800 hover:underline ml-auto"
                >
                  {tr('teacherRow.reset2fa')}
                </button>
                {reset2fa.error && (
                  <div className="basis-full text-xs text-red-600">{(reset2fa.error as Error).message}</div>
                )}
              </div>
            )}
            {canManage && (
              <ManagementActions
                currentRole={t.role}
                onChangeRole={(r) => changeRole.mutate(r)}
                onRemove={() => {
                  if (confirm(tr('teacherRow.removeConfirm', { name: t.displayName }))) {
                    remove.mutate()
                  }
                }}
                busy={changeRole.isPending || remove.isPending}
                error={(changeRole.error || remove.error) as Error | undefined}
              />
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function StatsContent({
  isLoading,
  data,
}: {
  isLoading: boolean
  data: TeacherStats | undefined
}) {
  const { t } = useTranslation()
  if (isLoading) return <span className="text-sm text-gray-500">{t('teacherRow.statsLoading')}</span>
  if (!data) return <span className="text-sm text-red-600">{t('teacherRow.statsError')}</span>
  return (
    <div className="flex gap-6 text-sm flex-wrap">
      <StatItem label={t('teacherRow.classes')} value={data.classesCount} />
      <StatItem label={t('teacherRow.students')} value={data.studentsCount} />
      <StatItem label={t('teacherRow.grades30d')} value={data.gradesLast30d} />
      {data.documentsCount !== undefined && (
        <StatItem label={t('teacherRow.documents')} value={data.documentsCount} />
      )}
    </div>
  )
}

function StatItem({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-lg font-bold text-primary">{value}</div>
    </div>
  )
}

function ManagementActions({
  currentRole,
  onChangeRole,
  onRemove,
  busy,
  error,
}: {
  currentRole: string
  onChangeRole: (newRole: string) => void
  onRemove: () => void
  busy: boolean
  error?: Error
}) {
  const { t } = useTranslation()
  const [newRole, setNewRole] = useState(currentRole)
  return (
    <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-3">
      <div className="text-xs uppercase text-gray-500 tracking-wider">{t('teacherRow.manage')}</div>
      <select
        className="input-field max-w-[180px] text-sm py-1"
        value={newRole}
        onChange={(e) => setNewRole(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        disabled={busy}
      >
        <option value="TEACHER">{t('teacherRow.roleTeacher')}</option>
        <option value="VICE_PRINCIPAL">{t('teacherRow.roleVicePrincipal')}</option>
        <option value="PRINCIPAL">{t('teacherRow.rolePrincipal')}</option>
      </select>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onChangeRole(newRole) }}
        disabled={busy || newRole === currentRole}
        className="text-xs btn-outline py-1"
      >
        {t('teacherRow.applyRole')}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove() }}
        disabled={busy}
        className="text-xs text-red-600 hover:text-red-800 hover:underline ml-auto"
      >
        {t('teacherRow.removeFromSchool')}
      </button>
      {error && (
        <div className="basis-full text-xs text-red-600 mt-1">{error.message}</div>
      )}
    </div>
  )
}
